import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Paper,
  Link as MuiLink
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import APIClient from '../../../utils/axios.util';
import { useAuth } from '../../Context/AuthContext';

// Validation Schema
const validationSchema = Yup.object().shape({
  companyEmail: Yup.string()
    .email('Email must be a valid email address.')
    .trim()
    .matches(/^[\w-.]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Email must be a valid email address.')
    .max(120, 'Email must be at most 120 characters long.') // Limit email length to 254 characters
    .required('Email is required.'),
  password: Yup.string()
    .matches(/^\S*$/, 'Password must not contain spaces.') // No spaces allowed
    .matches(/^[^A-Z]*[A-Z][^A-Z]*$/, 'Password must contain exactly one uppercase letter.') // Exactly one uppercase letter
    .matches(/^(?=.*[a-z])/, 'Password must contain at least one lowercase letter.') // At least one lowercase letter
    .matches(/^(?=.*\d)/, 'Password must contain at least one number.') // At least one number
    .matches(/^(?=.*[!@#$%^&*])/, 'Password must contain at least one special character.') // At least one special character
    .min(8, 'Password must be at least 8 characters long.') // Minimum password length of 8 characters
    .max(20, 'Password must be at most 20 characters long.')
    .required('Password is required.'),
});

const LoginForm = ({ onRegisterClick, setAlert, setAlertType, setAlertContent }) => {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const initialValues = {
    companyEmail: '',
    password: ''
  };

  const handleSubmit = (values, { setSubmitting, setErrors }) => {
    console.log('Login attempt:', values.companyEmail);
    const payload = {
      email: values.companyEmail,
      password: values.password
    };

    APIClient.post('/auth/login', payload)
      .then((response) => {
        console.log('✅ Login successful:', response.data);

        // Save user data to localStorage + sync AuthContext state
        login(response.data);
        setAlertContent('Login successful!');
        setAlertType('success');
        setAlert(true);

        // Admins always go straight to the dashboard
        const role = response.data?.user?.role;
        const accessStatus = response.data?.user?.access_status;
        if (role === 'admin') {
          navigate('/dashboard/demos');
        } else if (accessStatus === 'none') {
          navigate('/request-demos');
        } else {
          navigate('/dashboard/demos');
        }
      })
      .catch((error) => {
        console.error('❌ Login failed:', error);

        // const errorMessage =
        //   error?.response?.data?.message ||
        //   error?.response?.data?.detail ||
        //   'Login failed. Please check your credentials.';

        // single place to show error
        const detail = error?.response?.data?.detail ?? '';
        const isInactive =
          typeof detail === 'string' && detail.toLowerCase().includes('inactive');
        setAlertContent(
          isInactive
            ? 'Inactive user. Please contact support to reactivate your account.'
            : detail || 'Login failed. Please check your credentials.'
        );
        setAlertType('error');
        setAlert(true);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    // <Box
    //   sx={{
    //     display: 'flex',
    //     justifyContent: 'center',
    //     alignItems: 'center',
    //     minHeight: '100vh',
    //     backgroundColor: '#f5f5f5',
    //     padding: 2
    //   }}
    // >
    <Paper
      elevation={3}
      sx={{
        padding: 4,
        maxWidth: 450,
        width: '100%',
        borderRadius: 2
      }}
    >
      <Typography variant="h4" gutterBottom align="center" sx={{ mb: 1 }}>
        Welcome Back
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        Log in to access your demos
      </Typography>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
          <Form>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Email Field */}
              <TextField
                fullWidth
                id="companyEmail"
                name="companyEmail"
                label="Company Email"
                type="email"
                value={values.companyEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.companyEmail && Boolean(errors.companyEmail)}
                helperText={touched.companyEmail && errors.companyEmail}
                disabled={isSubmitting}
              />

              {/* Password Field */}
              <TextField
                fullWidth
                id="password"
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
                disabled={isSubmitting}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              {/* Login Button */}
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting}
                sx={{ mt: 1 }}
              >
                {isSubmitting ? 'Logging in...' : 'Log In'}
              </Button>

              {/* Register Link */}
              <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                Don't have an account?{' '}
                <MuiLink
                  component="button"
                  type="button"
                  variant="body2"
                  // onClick={() => navigate('/register')}
                  onClick={onRegisterClick}
                  sx={{ cursor: 'pointer' }}
                >
                  Register here
                </MuiLink>
              </Typography>
            </Box>
          </Form>
        )}
      </Formik>
    </Paper>
    // </Box>
  );
};

export default LoginForm;

